import { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Dimensions, PanResponder } from 'react-native';
import { useBreakpoint } from '../../../../hooks';
var INITIAL_WIDTH = Dimensions.get('window').width;
export var useCarousel = props => {
  var _a;
  var defaultSlide = props.defaultSlide,
    breakPoints = props.breakPoints,
    loop = props.loop,
    autoPlay = props.autoPlay,
    autoPlayDelay = props.autoPlayDelay,
    autoPlayTransitionSpeed = props.autoPlayTransitionSpeed,
    slideAnimationDuration = props.slideAnimationDuration,
    onIndexChanged = props.onIndexChanged,
    data = props.data,
    _props$initialWidth = props.initialWidth,
    initialWidth = _props$initialWidth === void 0 ? INITIAL_WIDTH : _props$initialWidth,
    _onPanResponderMove = props.onPanResponderMove,
    onPanResponderRelease = props.onPanResponderRelease;
  var _useBreakpoint = useBreakpoint({
      returnAsObject: true
    }),
    breakpoint = _useBreakpoint.breakpoint;
  var onMount = useRef(true);
  var _breakPoints$breakpoi = breakPoints[breakpoint],
    slidesPerPage = _breakPoints$breakpoi.slidesPerPage,
    slidesToScroll = _breakPoints$breakpoi.slidesToScroll,
    itemSpacing = _breakPoints$breakpoi.itemSpacing,
    seekGap = _breakPoints$breakpoi.seekGap,
    firstSeekGapSpacing = _breakPoints$breakpoi.firstSeekGapSpacing,
    lastSeekGapSpacing = _breakPoints$breakpoi.lastSeekGapSpacing;
  var _useState = useState(0),
    activeIndex = _useState[0],
    setActiveIndex = _useState[1];
  var _useState2 = useState(initialWidth),
    containerWidth = _useState2[0],
    setContainerWidth = _useState2[1];
  var autoplayTimeoutRef = useRef();
  var xPosition = useRef(0).current;
  var oldX = useRef(seekGap);
  var animatedValueX = useRef(0);
  var autoPlayDelayRef = useRef();
  var dataLength = data === null || data === void 0 ? void 0 : data.length;
  var childLengthWithoutFirstPage = dataLength - slidesPerPage;
  var childLength = Math.floor(childLengthWithoutFirstPage / slidesToScroll) + 1 || 0;
  var singleSlideWidth = containerWidth / slidesPerPage;
  singleSlideWidth -= itemSpacing / slidesPerPage * (slidesPerPage - 1);
  var hasDeltaSlide = dataLength % slidesToScroll % slidesPerPage;
  if (hasDeltaSlide) childLength += 1;
  if (seekGap) {
    singleSlideWidth -= seekGap * 2 / slidesPerPage + itemSpacing * 2 / slidesPerPage;
  }
  var getTransformValue = index => {
    var _isLastItem = childLength - 1 === index;
    var _isFirstItem = index === 0;
    var toValue = singleSlideWidth * slidesToScroll * index;
    if (seekGap) {
      toValue -= seekGap;
      if (_isFirstItem) {
        toValue += seekGap;
        if (firstSeekGapSpacing) toValue -= firstSeekGapSpacing;
      }
      if (_isLastItem) {
        toValue -= seekGap;
        if (lastSeekGapSpacing) toValue += lastSeekGapSpacing;
      }
    }
    if (itemSpacing) {
      toValue += itemSpacing * slidesToScroll * index;
      if (seekGap) {
        toValue -= itemSpacing;
        if (_isFirstItem) toValue += itemSpacing;
        if (_isLastItem) toValue -= itemSpacing;
      }
      if (_isLastItem && hasDeltaSlide) {
        toValue -= singleSlideWidth * (slidesToScroll - hasDeltaSlide);
        toValue -= itemSpacing * (slidesToScroll - hasDeltaSlide);
      }
    }
    return -toValue;
  };
  var animatedXPosition = useRef(new Animated.Value((_a = getTransformValue(0)) !== null && _a !== void 0 ? _a : 0)).current;
  var getPanResponderCallbacks = () => {
    return {
      onPanResponderTerminationRequest: () => false,
      onMoveShouldSetPanResponderCapture: (_, gestureState) => {
        var allow = Math.abs(gestureState.dx) > 10;
        if (allow) {
          stopAutoplay();
        }
        return allow;
      },
      onPanResponderMove: (_, gestureState) => {
        var newXPosition = oldX.current + gestureState.dx;
        Animated.timing(animatedXPosition, {
          toValue: newXPosition,
          useNativeDriver: false,
          duration: 0
        }).start();
        _onPanResponderMove === null || _onPanResponderMove === void 0 ? void 0 : _onPanResponderMove();
      },
      onPanResponderEnd: (_, gesture) => {
        stopAutoplay();
        startAutoplay();
        var correction = gesture.moveX - gesture.x0;
        changeIndex(correction > 0 ? -1 : 1);
        onPanResponderRelease === null || onPanResponderRelease === void 0 ? void 0 : onPanResponderRelease();
      }
    };
  };
  var panResponder = useMemo(() => PanResponder.create(getPanResponderCallbacks()), [activeIndex, containerWidth, slidesPerPage, slidesToScroll]);
  var startAutoplay = () => {
    if (autoPlayTransitionSpeed && autoPlay && containerWidth > 0) {
      stopAutoplay();
      autoplayTimeoutRef.current = setTimeout(autoplayTimeout, autoPlayTransitionSpeed);
    }
  };
  var stopAutoplay = () => {
    autoplayTimeoutRef.current && clearTimeout(autoplayTimeoutRef.current);
  };
  var goToNext = () => {
    goToNeighboring();
  };
  var goToPrev = () => {
    goToNeighboring(true);
  };
  var fixAndGo = delta => {
    changeIndex(delta);
  };
  var goToIndex = function goToIndex(index) {
    if (index === void 0) {
      index = 0;
    }
    var delta = index - activeIndex;
    fixAndGo(delta);
  };
  var autoplayTimeout = () => {
    goToNeighboring(autoPlayTransitionSpeed < 0);
  };
  var goToNeighboring = function goToNeighboring(toPrev) {
    if (toPrev === void 0) {
      toPrev = false;
    }
    fixAndGo(toPrev ? -1 : 1);
  };
  useEffect(() => {
    animatedXPosition.addListener(_ref => {
      var value = _ref.value;
      return animatedValueX.current = value;
    });
    return () => {
      animatedXPosition.removeAllListeners();
    };
  }, []);
  useEffect(() => {
    if (defaultSlide !== 0 && containerWidth !== 0) {
      goToIndex(defaultSlide);
    }
  }, [containerWidth]);
  useEffect(() => {
    if (!containerWidth) return;
    if (!autoPlayDelayRef.current) {
      autoPlayDelayRef.current = setTimeout(startAutoplay, autoPlayDelay);
    } else {
      startAutoplay();
    }
    return () => {
      stopAutoplay();
    };
  }, [activeIndex, containerWidth]);
  var changeIndex = function changeIndex(delta) {
    if (delta === void 0) {
      delta = 1;
    }
    var skipChanges = !delta;
    var calcDelta = delta;
    if (activeIndex <= 0 && delta < 0) {
      skipChanges = !loop;
      calcDelta = childLength - 1;
    } else if (activeIndex >= childLength - 1 && delta > 0) {
      skipChanges = !loop;
      calcDelta = -activeIndex;
    }
    var toValue = getTransformValue(activeIndex);
    if (skipChanges) return animateEndPosition(toValue);
    var newIndex = activeIndex + calcDelta;
    toValue = getTransformValue(newIndex);
    oldX.current = toValue;
    animateEndPosition(toValue);
    onIndexChanged === null || onIndexChanged === void 0 ? void 0 : onIndexChanged(newIndex, childLength);
    setActiveIndex(newIndex);
    stopAutoplay();
    startAutoplay();
  };
  var animateEndPosition = toValue => {
    if (onMount.current) {
      animatedXPosition.setValue(toValue);
      onMount.current = false;
    } else {
      Animated.timing(animatedXPosition, {
        duration: slideAnimationDuration,
        toValue: toValue,
        useNativeDriver: false
      }).start();
    }
  };
  var onLayoutContainer = _ref2 => {
    var _ref2$nativeEvent$lay = _ref2.nativeEvent.layout,
      x = _ref2$nativeEvent$lay.x,
      width = _ref2$nativeEvent$lay.width;
    xPosition = x;
    setContainerWidth(width);
    goToIndex(0);
  };
  var stateData = Object.assign(Object.assign({}, props), {
    activeIndex,
    animatedXPosition,
    childLength,
    containerWidth,
    itemSpacing,
    autoPlayTransitionSpeed,
    autoPlay,
    panResponder,
    singleSlideWidth,
    slidesPerPage,
    slidesToScroll,
    seekGap,
    xPosition,
    goToNext,
    goToPrev,
    goToIndex,
    onLayoutContainer
  });
  return stateData;
};